import React, { useContext, useState } from 'react'

import { Icon } from 'semantic-ui-react'
import { Card, CardActionArea, CardContent, CardMedia, Typography, Popover, Button, Box, Divider } from '@mui/material'

import { IProject } from '../../api/Data'
import { UserContext } from '../../contexts/UserContext'
import UpgradeTag from '../../components/common/UpgradeTag'

import moment from '../../utils/Moment'
import { formatNumber } from '../../utils/sharedFunctions'

const UploadsProjectCard = (props: IProps) => {
  const { userProfile } = useContext(UserContext)

  const { project, goToProjectShowPage, handleDuplicateProject, setShowDeletionConfModal } = props
  const { name, slug, imageUrl, deadline, shared, isProjectClosed, totalNumberOfTeamClips, clipMax, clipCount, pageViews } = project

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  
  const open = Boolean(anchorEl)

  const isUserCollaborator = (): boolean => {
    return userProfile?.userAccessRights === 'collaborator'
  }
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (action: string, slug: string) => {
    handleClose()

    switch (action) {
      case 'duplicate':
        handleDuplicateProject(slug)
        break
      case 'deletion':
        setShowDeletionConfModal(slug)
        break
      default:
    }
  }

return (
  <Card sx={{ paddingBottom: 1, opacity: shared ? 1 : 0.5 }}>
    <CardActionArea>
      <CardMedia
        component="img"
        height="140"
        image={imageUrl}
        alt="project image"
        onClick={() => goToProjectShowPage(slug)}
      />
    </CardActionArea >
    <CardContent sx={{ height: '120px', padding: '10px 10px 0 10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', overflow: 'hidden' }}>
        {name}
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Typography color="text.secondary">
          {isProjectClosed && (
            <Icon color="red" name="exclamation circle" />
          )}
          {deadline ? `End: ${moment(deadline).format('ll')}` : 'No expiry date'}
        </Typography>
        <Typography fontWeight={shared ? 'fontWeightBold' : 'normal'}>
          {shared ? 'Active' : 'Inactive'}
        </Typography>
      </Box>
    </CardContent>
      <Divider sx={{ my: 1 }} /> 
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: '4px 10px' }}>
        <Typography sx={{ color: totalNumberOfTeamClips >= clipMax ? 'red' : '' }}>
          {clipCount === 1 ? '1 clip' : `${formatNumber(clipCount || 0)} clips`}
          &nbsp;
          {totalNumberOfTeamClips >= clipMax && (
            <UpgradeTag text="Upgrade your plan to collect more video clips" style={{ fontWeight: 'bold' }} />
          )}
        </Typography>
        {!isUserCollaborator() && (
          <Button
            aria-controls="long-menu"
            aria-haspopup="true"
            onClick={handleClick}
            variant="contained"
            sx={{
              backgroundColor: '#6B50FF',
              fontSize: {
                xs: 12,
                sm: 14
              },
              fontWeight: 'bold',
              '&hover': {
                backgroundColor: '#13165a !important'
              }
            }}
          >
            MORE
          </Button>
        )}
      </Box>
      <Popover
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box display="flex" flexDirection="column" gap={1} padding={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontWeight: 'bold' }}>Page Views:</Typography>
            <Typography>{formatNumber(pageViews)}</Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleMenuItemClick('duplicate', slug)}
            sx={{
              width: '100%',
              justifyContent: 'center',
              backgroundColor: '#6b50ff',
              fontWeight: 'bold',
              '&hover': {
                backgroundColor: '#2531a0'}
              }
            }
          >
            Duplicate Project
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleMenuItemClick('deletion', slug)}
            sx={{
              width: '100%',
              justifyContent: 'center',
              backgroundColor: '#6b50ff',
              fontWeight: 'bold',
              '&hover': {
                backgroundColor: '#2531a0'
              }
            }}
          >
            Delete Project
          </Button>
        </Box>
      </Popover>
    </Card>
  )
}

interface IProps {
  project: IProject
  active: boolean
  handleDuplicateProject: (arg: string) => void
  goToProjectShowPage: (arg: string) => void
  setShowDeletionConfModal: (arg: string) => void
}

export default UploadsProjectCard
